import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';

import { selectEssSwitch } from 'shared/slice/essSwitchSlice';
import { ssrStateKey } from 'shared/hooks/useSsrState';
import { setSsrField } from 'shared/helpers/ssrCache';
import { useSsrElementsBank } from 'shared/hooks';

import styled, { css } from 'styled-components';
import { layerA180Deg, layerADisabled180Deg } from 'shared/styles/commonStyles';

import SSRDetailButtonContainer from './SSRDetailButtonContainer';
import HeaterRow from './HeaterRow';
import ConfirmSummary from './ConfirmSummary';
import { useHeaterEditor } from './useHeaterEditor';
import { MODES } from './heaterEditorReducer';
import {
  SSR_CURRENT_CAPACITY_A,
  exceedsCapacity,
} from 'shared/helpers/ssrCurrentBudget';
import { removeDeletedSpecs } from './heaterChanges';
import {
  addHeaterToSSRService,
  deleteHeaterFromSSRService,
} from 'shared/services/ssrs.service';
import { selectTgsSwitch } from 'shared/slice/tgsSwitchSlice';
import { selectTesSwitch } from 'shared/slice/tesSwitchSlice';
import MessageBox from '../userMessages/messageBox';

const samePartNumber = (a, b) =>
  String(a ?? '')
    .trim()
    .toUpperCase() ===
  String(b ?? '')
    .trim()
    .toUpperCase();

const AdminSSRItemDetails = ({
  isEnable,
  isFault,
  isWarn,
  data,
  handleButtonClick,
  id,
  location,
  machine,
  swtName,
  isMobile = false,
}) => {
  // The bank is the whole catalogue, fetched once and cached by react-query.
  // A miss against it means "no such heater", which is what lets the row
  // render a dropdown rather than a search box.
  const { partNumberSuggestions, elementsOptions } = useSsrElementsBank();
  const { specs } = data;

  const { flatEssSwitch, flatTgsSwitch, flatTesSwitch } = useSelector(
    swtName === 'ess'
      ? selectEssSwitch
      : swtName === 'tgs'
        ? selectTgsSwitch
        : selectTesSwitch
  );
  const switchData =
    swtName === 'ess'
      ? flatEssSwitch
      : swtName === 'tgs'
        ? flatTgsSwitch
        : flatTesSwitch;

  const { deviceMac } = switchData[location][machine];

  const {
    mode,
    rows,
    changes,
    hasChanges,
    enterEdit,
    enterAdd,
    cancel,
    setPartNumber,
    removeRow,
  } = useHeaterEditor(specs);

  const [pendingConfirm, setPendingConfirm] = useState(false);
  const [activateMessageBox, setActivateMessageBox] = useState(false);
  const [message, setMessage] = useState(null);
  const queryClient = useQueryClient();

  // Look a part number up in the catalogue. A miss means the heater is not in
  // the catalogue at all — either it was retired, or the fetch failed.
  //
  // Matched by part number rather than by index into a parallel array:
  // deriveElementsBank reverses elementsOptions but not partNumberSuggestions,
  // and dedupes the latter, so the two do not line up position for position.
  const resolveSpec = (partNumber) =>
    elementsOptions.find((element) =>
      samePartNumber(element?.partNumber, partNumber)
    ) ?? null;

  // Fall back to the spec the SSR already carries before giving up and showing
  // dashes, so a heater absent from the catalogue still displays.
  const specFor = (partNumber) =>
    resolveSpec(partNumber) ||
    specs.find((spec) => samePartNumber(spec?.partNumber, partNumber)) ||
    {};

  const buttonsDisabled =
    mode === MODES.VIEW ? ['clear', 'apply'] : ['edit', 'add'];

  const handleButtonPress = (name) => {
    if (name === 'edit') {
      enterEdit();
    } else if (name === 'add') {
      enterAdd();
    } else if (name === 'clear') {
      cancel();
    } else if (name === 'apply') {
      // Checked before hasChanges: a row left blank produces no diff at all,
      // so otherwise the admin would be told "no changes" when the real
      // problem is an unanswered dropdown. Removing a heater is the remove
      // button, never an empty selection.
      if (rows.some((row) => !row.partNumber.trim())) {
        setMessage(
          'please select a part number for every heater, or remove the row'
        );
        setActivateMessageBox(true);
        return;
      }
      if (!hasChanges) {
        setMessage('no changes to apply');
        setActivateMessageBox(true);
        cancel();
        return;
      }
      // An SSR is filled to a current budget. Check what the SSR would carry
      // after this edit, before anything reaches the device.
      const proposedSpecs = rows.map((row) => specFor(row.partNumber));
      if (exceedsCapacity(proposedSpecs)) {
        setMessage(
          `these heaters draw more than the ${SSR_CURRENT_CAPACITY_A}a this SSR can carry`,
        );
        setActivateMessageBox(true);
        return;
      }
      setPendingConfirm(true);
    }
  };

  const handleConfirm = async () => {
    setPendingConfirm(false);

    const addedSpecs = changes.toAdd.map(resolveSpec);
    if (addedSpecs.some((spec) => !spec?.current)) {
      setMessage(
        'element not found! please check again the input specifications'
      );
      setActivateMessageBox(true);
      return;
    }

    const request = { ssr_no: id - 1, deviceType: swtName.toUpperCase() };

    try {
      // toAdd and toDelete carry one entry per copy, so a heater repeated
      // three times on the SSR issues three requests. This assumes each call
      // adds or removes a single occurrence rather than every copy sharing
      // that part number.
      await Promise.all([
        ...changes.toAdd.map((partNumber) =>
          addHeaterToSSRService(deviceMac, { ...request, partNumber })
        ),
        ...changes.toDelete.map((partNumber) =>
          deleteHeaterFromSSRService(deviceMac, { ...request, partNumber })
        ),
      ]);
    } catch (error) {
      // Some writes may have landed. Write nothing to the cache and let the
      // next poll or socket update supply the truth.
      setMessage(
        error?.message || 'could not save heater changes, please try again'
      );
      setActivateMessageBox(true);
      cancel();
      return;
    }

    // Only mirror into the cache once the device has accepted the change.
    const nextSpecs = [
      ...removeDeletedSpecs(specs, changes.toDelete),
      ...addedSpecs,
    ];

    queryClient.setQueryData(ssrStateKey(machine), (prev) =>
      setSsrField(
        prev,
        `ssr${id}`,
        'specs',
        nextSpecs.length !== 0 ? nextSpecs : [{}]
      )
    );
    cancel();
    setMessage(null);
    setActivateMessageBox(true);
  };

  // ********************************part number picker***********************
  // A heater already on this SSR may have since left the catalogue. Without an
  // option for it the row would show nothing and silently swap it out.
  const optionsFor = (partNumber) =>
    partNumber &&
    !partNumberSuggestions.some((s) => samePartNumber(s, partNumber))
      ? [...partNumberSuggestions, partNumber]
      : partNumberSuggestions;
  // ********************************part number picker***********************

  return (
    <Wrapper isMobile={isMobile}>
      <ContentWrapper
        isEnable={isEnable}
        isFault={isFault}
        isWarn={isWarn}
        isMobile={isMobile}
      >
        {rows.length === 0 && (
          <HeaterRow
            partNumber=''
            spec={{}}
            isLocked
            onChange={() => {}}
            isMobile={isMobile}
          />
        )}
        {rows.map((row, index) => (
          <HeaterRow
            key={index}
            partNumber={row.partNumber}
            spec={specFor(row.partNumber)}
            isEnable={isEnable}
            isLocked={row.locked}
            options={optionsFor(row.partNumber)}
            onChange={(value) => setPartNumber(index, value)}
            onRemove={() => removeRow(index)}
            isMobile={isMobile}
          />
        ))}
      </ContentWrapper>

      <SSRDetailButtonContainer
        handleClick={handleButtonPress}
        buttons={['edit', 'add', 'clear', 'apply']}
        disabled={buttonsDisabled}
        isMobile={isMobile}
      />

      {pendingConfirm && (
        <MessageBoxWrapper isMobile={isMobile}>
          <ConfirmSummary
            changes={changes}
            describeHeater={(partNumber) => {
              const spec = specFor(partNumber);
              return spec?.current
                ? `${partNumber}  ${spec.current}a  ${spec.wattage}w`
                : partNumber;
            }}
            onConfirm={handleConfirm}
            onCancel={() => {
              setPendingConfirm(false);
              cancel();
            }}
          />
        </MessageBoxWrapper>
      )}

      {activateMessageBox && (
        <MessageBoxWrapper isMobile={isMobile}>
          <MessageBox
            onClose={() => {
              setActivateMessageBox(false);
              if (!message) {
                handleButtonClick();
              }
            }}
            title='ssr details settings'
            messages={message ? [message] : ['selections confirmed']}
          />
        </MessageBoxWrapper>
      )}
    </Wrapper>
  );
};
export default AdminSSRItemDetails;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  ${(p) =>
    p.isMobile &&
    css`
      width: 100%;
      align-items: stretch;
      position: relative;
    `}
`;

const ContentWrapper = styled.ul`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  padding: 1.5px 0;
  border-radius: 12px 12px 0 12px;

  ${layerA180Deg};

  ${(p) =>
    p.isEnable ||
    css`
      ${layerADisabled180Deg};
      border-radius: 12px;
    `}

  ${(p) =>
    p.isFault &&
    css`
      border: 1px solid red;
    `};

  ${(p) =>
    p.isWarn &&
    css`
      border: 1px solid #ff7800;
    `};

  ${(p) =>
    p.isMobile &&
    css`
      padding: 4px;
      border-radius: 10px;
    `}
`;

const MessageBoxWrapper = styled.div`
  width: 120%;
  border-radius: 16px;

  display: flex;
  justify-content: center;

  margin-top: 8px;
  padding: 16px;

  position: absolute;
  top: -100px;
  right: 0px;
  z-index: 100;

  cursor: pointer;

  ${(p) =>
    p.isMobile &&
    css`
      width: 100%;
      top: 0;
      right: auto;
      left: 0;
      margin-top: 0;
      padding: 0;
    `}
`;
